import { TouchpointDTOBatchJobExecutionStatus } from "generated/schemas";

import { TouchpointType } from "types/touchpoint";

type EmailWriterTouchpoint = {
  id?: string;
  name?: string;
  type: string;
  batchJobExecutionStatus?: TouchpointDTOBatchJobExecutionStatus;
};

export const isEmailWriterJobRunning = ({
  isTemplateCampaign,
  touchpointType,
  batchJobExecutionStatus,
}: {
  isTemplateCampaign: boolean;
  touchpointType: string;
  batchJobExecutionStatus?: TouchpointDTOBatchJobExecutionStatus;
}): boolean =>
  isTemplateCampaign &&
  touchpointType === TouchpointType.EMAIL &&
  batchJobExecutionStatus === TouchpointDTOBatchJobExecutionStatus.RUNNING;

export const hasRunningEmailWriterTouchpoints = (
  touchpoints: EmailWriterTouchpoint[] | undefined,
): boolean =>
  touchpoints?.some((touchpoint) =>
    isEmailWriterJobRunning({
      isTemplateCampaign: true,
      touchpointType: touchpoint.type,
      batchJobExecutionStatus: touchpoint.batchJobExecutionStatus,
    }),
  ) ?? false;

const getEmailWriterTouchpoints = (
  touchpoints: EmailWriterTouchpoint[] | undefined,
): EmailWriterTouchpoint[] =>
  touchpoints?.filter((touchpoint) => touchpoint.type === TouchpointType.EMAIL) ?? [];

/** Emails that participated in a writer/rewrite job (status set by the batch pipeline). */
const getEmailWriterTouchpointsWithJobStatus = (
  touchpoints: EmailWriterTouchpoint[] | undefined,
): EmailWriterTouchpoint[] =>
  getEmailWriterTouchpoints(touchpoints).filter(
    (touchpoint) => touchpoint.batchJobExecutionStatus != null,
  );

export const getSuccessfulEmailWriterTouchpoints = (
  touchpoints: EmailWriterTouchpoint[] | undefined,
): EmailWriterTouchpoint[] =>
  getEmailWriterTouchpointsWithJobStatus(touchpoints).filter(
    (touchpoint) =>
      touchpoint.batchJobExecutionStatus === TouchpointDTOBatchJobExecutionStatus.SUCCESSFUL,
  );

const isTerminalEmailWriterStatus = (
  status: TouchpointDTOBatchJobExecutionStatus | undefined,
): boolean =>
  status === TouchpointDTOBatchJobExecutionStatus.SUCCESSFUL ||
  status === TouchpointDTOBatchJobExecutionStatus.FAILED;

const hasEmailWriterJobActivity = (touchpoints: EmailWriterTouchpoint[]): boolean =>
  touchpoints.some(
    (touchpoint) =>
      touchpoint.batchJobExecutionStatus === TouchpointDTOBatchJobExecutionStatus.RUNNING ||
      isTerminalEmailWriterStatus(touchpoint.batchJobExecutionStatus),
  );

export type EmailWriterPollingResolution = "wait" | "poll" | "clear" | "complete";

export const resolveEmailWriterPollingSession = ({
  touchpoints,
  toastAlreadyShown,
}: {
  touchpoints: EmailWriterTouchpoint[] | undefined;
  toastAlreadyShown: boolean;
}): EmailWriterPollingResolution => {
  if (!touchpoints) {
    return "wait";
  }

  const emailTouchpoints = getEmailWriterTouchpoints(touchpoints);

  if (emailTouchpoints.length === 0) {
    return touchpoints.length > 0 ? "clear" : "poll";
  }

  if (hasRunningEmailWriterTouchpoints(touchpoints)) {
    return "poll";
  }

  // Rewrite may run on a single email while siblings have no job status — only consider
  // touchpoints that participated in a writer/rewrite batch job.
  const emailsWithJobStatus = getEmailWriterTouchpointsWithJobStatus(touchpoints);

  if (emailsWithJobStatus.length === 0) {
    return "clear";
  }

  const allSuccessful = emailsWithJobStatus.every(
    (touchpoint) =>
      touchpoint.batchJobExecutionStatus === TouchpointDTOBatchJobExecutionStatus.SUCCESSFUL,
  );
  const anyFailed = emailsWithJobStatus.some(
    (touchpoint) =>
      touchpoint.batchJobExecutionStatus === TouchpointDTOBatchJobExecutionStatus.FAILED,
  );

  if (anyFailed) {
    return "clear";
  }

  if (allSuccessful) {
    return toastAlreadyShown ? "clear" : "complete";
  }

  if (!hasEmailWriterJobActivity(emailsWithJobStatus)) {
    return "clear";
  }

  return "poll";
};

export const shouldContinueEmailWriterTouchpointPolling = (
  touchpoints: EmailWriterTouchpoint[] | undefined,
): boolean => {
  const resolution = resolveEmailWriterPollingSession({
    touchpoints,
    toastAlreadyShown: false,
  });

  return resolution === "poll" || resolution === "wait";
};

export const shouldShowEmailWriterCompletionToast = ({
  wasPollingEmailWriter,
  hadRunningEmailWriterTouchpoints,
  hasRunningEmailWriterTouchpoints: hasRunningEmailWriter,
  touchpoints,
}: {
  wasPollingEmailWriter: boolean;
  hadRunningEmailWriterTouchpoints: boolean;
  hasRunningEmailWriterTouchpoints: boolean;
  touchpoints: EmailWriterTouchpoint[] | undefined;
}): boolean => {
  if (!wasPollingEmailWriter || !hadRunningEmailWriterTouchpoints || hasRunningEmailWriter) {
    return false;
  }

  const emailsWithJobStatus = getEmailWriterTouchpointsWithJobStatus(touchpoints);

  if (emailsWithJobStatus.length === 0) {
    return false;
  }

  if (
    emailsWithJobStatus.some(
      (touchpoint) =>
        touchpoint.batchJobExecutionStatus === TouchpointDTOBatchJobExecutionStatus.FAILED,
    )
  ) {
    return false;
  }

  return emailsWithJobStatus.every(
    (touchpoint) =>
      touchpoint.batchJobExecutionStatus === TouchpointDTOBatchJobExecutionStatus.SUCCESSFUL,
  );
};
